import React, { useEffect, useState } from "react";
import { useUI } from "../context/UIContext";
import { ReportAPI, ProjectAPI } from "../api/apiClient";
import { toast } from "react-hot-toast";
import PeriodReport from "./PeriodReport";
import SnapshotReport from "./SnapshotReport";

type SnapshotResult = any;
type PeriodResult = any;

type ReportId =
  | "snapshot"
  | "period"
  | "task"
  | "gantt"
  | "works"
  | "group"
  | "markwise"
  | "material"
  | "material-vendor"
  | "manpower-vendor"
  | "manpower-resource"
  | "equipment-resource"
  | "equipment-vendor"
  | "drawings"
  | "weather";

type ReportItem = {
  id: ReportId;
  name: string;
  description: string;
  icon: React.ReactNode;
};

type ReportSection = {
  title: string;
  description: string;
  items: ReportItem[];
};

const iconClass = "h-5 w-5";

const icons = {
  camera: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 8.5h3l1.5-2h7l1.5 2h3v10H4v-10Z" strokeLinejoin="round" />
      <circle cx="12" cy="13.5" r="3" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 7h14v12H5zM8 4v5M16 4v5M5 11h14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  clipboard: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M9 5h6M9 4h6v3H9zM7 6H5v14h14V6h-2M8 11h8M8 15h6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 19V9h3v10M10.5 19V5h3v14M16 19v-7h3v7M3 19h18" strokeLinejoin="round" />
    </svg>
  ),
  trend: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="m4 16 5-5 4 3 7-8M15 6h5v5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  layers: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="m12 4 9 5-9 5-9-5 9-5ZM5 13l7 4 7-4M5 17l7 4 7-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  box: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="m4 8 8-4 8 4-8 4-8-4ZM4 8v8l8 4 8-4V8M12 12v8" strokeLinejoin="round" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3.5 19c.5-3 2.3-4.5 5.5-4.5s5 1.5 5.5 4.5M14 15c3.5-.5 5.5 1 6 4" strokeLinecap="round" />
    </svg>
  ),
  tool: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M14 7a4 4 0 0 0-5-4l2.5 2.5-2 2L7 5a4 4 0 0 0 4 5L5 16l3 3 6-6a4 4 0 0 0 5-5l-2.5 2.5-2-2L17 6a4 4 0 0 0-3 1Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  drawing: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 20 12 4l7 16H5ZM12 9v7M9 16h6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  cloud: (
    <svg viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M7 18h11a4 4 0 0 0 .5-8 6 6 0 0 0-11-1.5A4.8 4.8 0 0 0 7 18Z" strokeLinecap="round" />
      <path d="M8 21v-1M12 21v-1M16 21v-1" />
    </svg>
  ),
};

export default function ProjectReports() {
  const {
    selectedProjectId,
    selectedProjectName,
    setViewMode,
    pushUIState,
  } = useUI();

  const [reportType, setReportType] = useState<
    "menu" | "snapshot" | "period" | "gantt" | "snapshot-results" | "period-results"
  >("menu");

  const today = new Date().toISOString().split("T")[0];
  const todayDate = new Date();
  const lastWeekDate = new Date();
  lastWeekDate.setDate(todayDate.getDate() - 7);

  const defaultTo = todayDate.toISOString().split("T")[0];
  const defaultFrom = lastWeekDate.toISOString().split("T")[0];

  const [snapshotDate, setSnapshotDate] = useState<string>(today);
  const [taskGroup, setTaskGroup] = useState("");

  const [taskGroups, setTaskGroups] = useState<
    { taskGroupNo: number; taskGroupName: string }[]
  >([]);

  const [fromDate, setFromDate] = useState<string>(defaultFrom);
  const [toDate, setToDate] = useState<string>(defaultTo);

  const [snapshotResult, setSnapshotResult] = useState<SnapshotResult | null>(null);
  const [periodResult, setPeriodResult] = useState<PeriodResult | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!selectedProjectId) return;

    ProjectAPI.getTaskGroups(selectedProjectId)
      .then(setTaskGroups)
      .catch(console.error);
  }, [selectedProjectId]);

  if (!selectedProjectId) {
    return <div className="p-4 text-red-600">No project selected.</div>;
  }

  // ------------------------------------------------------------
  // Existing API actions - preserved
  // ------------------------------------------------------------

  const runSnapshot = async () => {
    if (!snapshotDate) return toast.error("Select a date");

    try {
      const res = await ReportAPI.getSnapshotReport(
        selectedProjectId,
        snapshotDate,
        taskGroup
      );

      setSnapshotResult(res);
      setReportType("snapshot-results");
    } catch (err: any) {
      toast.error(err.message || "Snapshot failed");
    }
  };

  const runPeriod = async () => {
    if (!fromDate || !toDate) {
      return toast.error("Select both dates");
    }

    try {
      const res = await ReportAPI.getPeriodReport(
        selectedProjectId,
        fromDate,
        toDate,
        taskGroup
      );

      setPeriodResult(res);
      setReportType("period-results");
    } catch (err: any) {
      toast.error(err.message || "Period report failed");
    }
  };

  // ------------------------------------------------------------
  // Existing report navigation - preserved
  // ------------------------------------------------------------

  const openExistingReport = (id: ReportId) => {
    switch (id) {
      case "snapshot":
        setReportType("snapshot");
        return;

      case "period":
        setReportType("period");
        return;

      case "gantt": {
        setReportType("gantt");
        return;
      }

      case "task":
        pushUIState();
        setViewMode("task-report");
        return;

      case "works":
        pushUIState();
        setViewMode("works-progress-report");
        return;

      case "group":
        pushUIState();
        setViewMode("group-report");
        return;

      case "markwise": {
        const url = `/markwise-progress?projectId=${selectedProjectId}&projectName=${encodeURIComponent(
          selectedProjectName || ""
        )}`;
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.location.href = url;
        }
        return;
      }

      case "material":
        pushUIState();
        setViewMode("material-statement");
        return;

      case "material-vendor":
        pushUIState();
        setViewMode("material-statement-vendor-wise");
        return;

      case "manpower-vendor":
        pushUIState();
        setViewMode("manpower-statement-vendor-wise");
        return;

      case "manpower-resource":
        pushUIState();
        setViewMode("manpower-statement-resource-wise");
        return;

      case "equipment-resource":
        pushUIState();
        setViewMode("equipment-statement-resource-wise");
        return;

      case "equipment-vendor":
        pushUIState();
        setViewMode("equipment-statement-vendor-wise");
        return;

      case "drawings":
        pushUIState();
        setViewMode("drawings-report");
        return;

      case "weather":
        pushUIState();
        setViewMode("weather-keyword-report");
        return;

      default:
        return;
    }
  };

  const sections: ReportSection[] = [
    {
      title: "Issue reports",
      description: "Review issue status, activity, and delivery windows.",
      items: [
        {
          id: "snapshot",
          name: "Snapshot report",
          description: "Status as of a selected date",
          icon: icons.camera,
        },
        {
          id: "period",
          name: "Period report",
          description: "Changes between two dates",
          icon: icons.calendar,
        },
        {
          id: "task",
          name: "Task report",
          description: "Detailed task-level overview",
          icon: icons.clipboard,
        },
      ],
    },
    {
      title: "Progress reports",
      description: "Understand schedule health and completion trends.",
      items: [
        {
          id: "gantt",
          name: "Gantt chart",
          description: "Project schedule and dependencies",
          icon: icons.chart,
        },
        {
          id: "works",
          name: "Works progress",
          description: "Work completion overview",
          icon: icons.trend,
        },
        {
          id: "group",
          name: "Group progress",
          description: "Progress by task group",
          icon: icons.layers,
        },
        {
          id: "markwise",
          name: "Markwise progress",
          description: "Track progress by mark",
          icon: icons.chart,
        },
      ],
    },
    {
      title: "Statement reports",
      description: "Analyze materials, manpower, and equipment.",
      items: [
        {
          id: "material",
          name: "Material statement",
          description: "Material usage summary",
          icon: icons.box,
        },
        {
          id: "material-vendor",
          name: "Material by vendor",
          description: "Vendor-level material data",
          icon: icons.box,
        },
        {
          id: "manpower-vendor",
          name: "Manpower by vendor",
          description: "Workforce across vendors",
          icon: icons.users,
        },
        {
          id: "manpower-resource",
          name: "Manpower by resource",
          description: "Workforce by resource type",
          icon: icons.users,
        },
        {
          id: "equipment-resource",
          name: "Equipment by resource",
          description: "Equipment allocation",
          icon: icons.tool,
        },
        {
          id: "equipment-vendor",
          name: "Equipment by vendor",
          description: "Vendor equipment summary",
          icon: icons.tool,
        },
      ],
    },
    {
      title: "Information reports",
      description: "Access supporting project records and site data.",
      items: [
        {
          id: "drawings",
          name: "Drawings report",
          description: "Drawing status and revisions",
          icon: icons.drawing,
        },
        {
          id: "weather",
          name: "Weather report",
          description: "Weather events and impact",
          icon: icons.cloud,
        },
      ],
    },
  ];

  // ------------------------------------------------------------
  // Figma Make style report menu
  // ------------------------------------------------------------

  const showMenu = (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <div className="mb-8 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
        <div>
          <p className="mb-2 text-sm font-semibold text-blue-600">
            {selectedProjectName || "Selected project"}
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-950">
            Project reports
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Generate clear, up-to-date reports for project progress, resources,
            and site activity.
          </p>
        </div>

        <label className="relative block w-full sm:w-72">
          <span className="sr-only">Search reports</span>

          <svg
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="6" />
            <path d="m16 16 4 4" strokeLinecap="round" />
          </svg>

          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search reports"
            className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
          />
        </label>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        {sections.map((section) => {
          const visibleItems = section.items.filter((item) =>
            `${item.name} ${item.description}`
              .toLowerCase()
              .includes(search.toLowerCase())
          );

          if (visibleItems.length === 0) return null;

          return (
            <section
              key={section.title}
              className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
            >
              <div className="mb-5">
                <h2 className="text-base font-bold text-slate-900">
                  {section.title}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {section.description}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {visibleItems.map((report) => (
                  <button
                    type="button"
                    key={report.id}
                    onClick={() => openExistingReport(report.id)}
                    className="group flex min-h-28 items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-left transition hover:-translate-y-0.5 hover:border-blue-200 hover:bg-blue-50/60 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-slate-600 shadow-sm ring-1 ring-slate-200 transition group-hover:text-blue-700 group-hover:ring-blue-200">
                      {report.icon}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-900">
                        {report.name}
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-slate-500">
                        {report.description}
                      </span>
                    </span>

                    <svg
                      viewBox="0 0 24 24"
                      className="mt-1 h-4 w-4 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        d="m9 18 6-6-6-6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );

  // ------------------------------------------------------------
  // Snapshot input - existing API preserved, UI modernized
  // ------------------------------------------------------------

  const snapshotInput = (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <button
        type="button"
        onClick={() => setReportType("menu")}
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-slate-900"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            d="m15 18-6-6 6-6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        All reports
      </button>

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-6 sm:px-8">
          <div className="mb-5 grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-700">
            {icons.camera}
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-slate-950">
            Snapshot report
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-500">
            Configure this report for{" "}
            <strong className="font-semibold text-slate-700">
              {selectedProjectName}
            </strong>
            .
          </p>
        </div>

        <div className="space-y-6 p-6 sm:p-8">
          <DateField
            label="Snapshot date"
            value={snapshotDate}
            onChange={setSnapshotDate}
          />

          <TaskGroupField
            value={taskGroup}
            onChange={setTaskGroup}
            taskGroups={taskGroups}
          />

          <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setReportType("menu")}
              className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={runSnapshot}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              Run report
              <ArrowRightIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ------------------------------------------------------------
  // Period input - existing API preserved, UI modernized
  // ------------------------------------------------------------

  const periodInput = (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <button
        type="button"
        onClick={() => setReportType("menu")}
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-slate-900"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            d="m15 18-6-6 6-6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        All reports
      </button>

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-6 sm:px-8">
          <div className="mb-5 grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-700">
            {icons.calendar}
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-slate-950">
            Period report
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-500">
            Configure this report for{" "}
            <strong className="font-semibold text-slate-700">
              {selectedProjectName}
            </strong>
            .
          </p>
        </div>

        <div className="space-y-6 p-6 sm:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <DateField
              label="From date"
              value={fromDate}
              onChange={setFromDate}
            />

            <DateField
              label="To date"
              value={toDate}
              onChange={setToDate}
            />
          </div>

          <TaskGroupField
            value={taskGroup}
            onChange={setTaskGroup}
            taskGroups={taskGroups}
          />

          <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setReportType("menu")}
              className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={runPeriod}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              Run report
              <ArrowRightIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ------------------------------------------------------------
  // Gantt input - existing new-tab navigation preserved
  // ------------------------------------------------------------

  const ganttInput = (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <button
        type="button"
        onClick={() => setReportType("menu")}
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-slate-900"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            d="m15 18-6-6 6-6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        All reports
      </button>

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-6 sm:px-8">
          <div className="mb-5 grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-700">
            {icons.chart}
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-slate-950">
            Gantt chart
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-500">
            Configure the schedule date for{" "}
            <strong className="font-semibold text-slate-700">
              {selectedProjectName}
            </strong>
            .
          </p>
        </div>

        <div className="space-y-6 p-6 sm:p-8">
          <DateField
            label="As on date"
            value={toDate}
            onChange={setToDate}
          />

          <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setReportType("menu")}
              className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={() => {
                if (!toDate) {
                  toast.error("Select a date");
                  return;
                }

                const url = `/gantt?date=${toDate}&projectId=${selectedProjectId}&projectName=${encodeURIComponent(
                  selectedProjectName || ""
                )}`;

                const newTab = window.open("", "_blank");

                if (newTab) {
                  newTab.location.href = url;
                }
              }}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              Run report
              <ArrowRightIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ------------------------------------------------------------
  // Existing result screens - preserved
  // ------------------------------------------------------------

  const snapshotResults = snapshotResult && (
    <div className="p-6">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-950">
            Snapshot report result
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {selectedProjectName} · {snapshotDate}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setReportType("menu")}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
        >
          Back to reports
        </button>
      </div>

      <SnapshotReport
        report={snapshotResult}
        snapshotDate={snapshotDate}
        projectName={selectedProjectName}
      />
    </div>
  );

  const periodResults = periodResult && (
    <div className="p-6">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-950">
            Period report result
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {selectedProjectName} · {fromDate} to {toDate}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setReportType("menu")}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
        >
          Back to reports
        </button>
      </div>

      <PeriodReport
        report={periodResult}
        projectName={selectedProjectName ?? ""}
        fromDate={fromDate}
        toDate={toDate}
      />
    </div>
  );

  switch (reportType) {
    case "menu":
      return showMenu;

    case "snapshot":
      return snapshotInput;

    case "period":
      return periodInput;

    case "gantt":
      return ganttInput;

    case "snapshot-results":
      return snapshotResults;

    case "period-results":
      return periodResults;

    default:
      return showMenu;
  }
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-slate-700">
        {label}
      </span>

      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      />
    </label>
  );
}

function TaskGroupField({
  value,
  onChange,
  taskGroups,
}: {
  value: string;
  onChange: (value: string) => void;
  taskGroups: { taskGroupNo: number; taskGroupName: string }[];
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-slate-700">
        Task group
      </span>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      >
        <option value="">All task groups</option>

        {taskGroups.map((group) => (
          <option key={group.taskGroupNo} value={group.taskGroupNo}>
            {group.taskGroupName}
          </option>
        ))}
      </select>
    </label>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        d="m9 18 6-6-6-6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
